import { cloneDeep } from 'lodash';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { SnAbilityService } from '@lib/ability';
import { ISnListResponse } from '@lib/api';
import { t } from '@lib/translation';
import { SnActionConfig } from '@lib/ui/actions';
import { SnDataListComponent } from '@lib/ui/data-list';

import {
  LprHotListAPI,
  LprHotlistGroupsAPI,
  SnLpr,
  SnLprHotListGroup,
  SnLprObject,
} from '../../api';
import { AddPlateToGroupDialogComponent } from '../add-plate-to-group-dialog/add-plate-to-group-dialog.component';

@Component({
  templateUrl: './manage-lpr-group-dialog.component.html',
  styleUrls: ['./manage-lpr-group-dialog.component.scss'],
})
export class ManageLprGroupDialogComponent implements OnInit, OnDestroy {
  @ViewChild('list', { static: false })
  public readonly list: SnDataListComponent<SnLprObject>;
  public saving: boolean;

  public readonly loadData = this._loadData.bind(this);
  public readonly selectedPlates = this._selectedPlates.bind(this);

  public formModel: any = {};
  public _querySearch: string;
  public groupIsNull: boolean = false;
  public readonly searchControl: UntypedFormControl = new UntypedFormControl();
  public readonly columns: string[] = ['plate', 'action'];
  public readonly canCreate: boolean;
  public readonly canChange: boolean;
  public readonly canDelete: boolean;
  public readonly actions: SnActionConfig<SnLprObject>[];

  private readonly _destroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      lprHotlist: SnLpr;
      lprGroup: SnLprHotListGroup;
      lprObjects: SnLprObject;
    },
    private readonly _dialogRef: MatDialogRef<ManageLprGroupDialogComponent>,
    private readonly _lprGroupsAPI: LprHotlistGroupsAPI,
    private readonly _lprHotlistAPI: LprHotListAPI,
    private readonly _matDialog: MatDialog,
    ability: SnAbilityService,
  ) {
    this.canCreate = ability.can({
      subject: 'lpr/lprhotlistobject',
      action: 'add',
    });
    this.canChange = ability.can({
      subject: 'lpr/lprhotlistobject',
      action: 'change',
    });
    this.canDelete = ability.can({
      subject: 'lpr/lprhotlistobject',
      action: 'delete',
    });
    this.actions = this._genActions();
  }

  public ngOnInit(): void {
    this.formModel = cloneDeep(this.data.lprGroup) || {};
    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this._destroy$))
      .subscribe((v) => {
        this._querySearch = v;
        this._reloadData();
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submit(): void {
    this.saving = true;
    const data = {
      ...this.data.lprGroup,
    };
    this._lprGroupsAPI
      .update(this.formModel.id, data)
      .pipe(takeUntil(this._destroy$))
      .subscribe((group) => {
        this.saving = false;
        this._dialogRef.close(group);
      });
  }

  public addPlate(): void {
    const dialogRef = this._matDialog.open(AddPlateToGroupDialogComponent, {
      data: {
        group: this.data.lprGroup,
        list: this.data.lprHotlist,
      },
      width: '100%',
      maxWidth: '600px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((plate) => {
        if (!plate) {
          return;
        }
        this._reloadData();
      });
  }

  public clear(): void {
    this.searchControl.reset(null);
  }

  public isSelected(object: SnLprObject): boolean {
    return this.formModel.lprHotlistObjects.findIndex((obj) => obj.id === object.id) > -1;
  }

  private _selectedPlates(): Observable<ISnListResponse<SnLprHotListGroup>> {
    return of({
      count: this.formModel.lprHotlistObjects.length,
      totalPages: 1,
      results: this.formModel.lprHotlistObjects,
    }).pipe(takeUntil(this._destroy$));
  }

  private _reloadData(): void {
    this.list.reference.reload();
  }

  private _loadData(): Observable<ISnListResponse<SnLprObject>> {
    if (this.data.lprGroup.id === null) {
      this.groupIsNull = true;
    }
    return this._lprHotlistAPI.getGroupsLprPlates({
      group: this.data.lprGroup.id,
      lpr_hotlist: this.data.lprHotlist.id,
      group__isnull: this.groupIsNull,
      search: this._querySearch,
    });
  }

  private _editPlate(item: SnLprObject): void {
    const dialogRef = this._matDialog.open(AddPlateToGroupDialogComponent, {
      data: {
        group: this.data.lprGroup,
        list: this.data.lprHotlist,
        item,
      },
      width: '100%',
      maxWidth: '600px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((plate) => {
        if (!plate) {
          return;
        }
        this._reloadData();
      });
  }

  private _deletePlate(item: SnLprObject): void {
    this._lprHotlistAPI
      .removePlateFromLprObject(item.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this._reloadData());
  }

  private _genActions(): SnActionConfig<SnLprObject>[] {
    return [
      {
        label: t('common.edit'),
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-pen',
        },
        click: this._editPlate.bind(this),
        hidden: !this.canChange,
      },
      {
        label: t('common.delete'),
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-trash-alt',
        },
        color: 'warn',
        click: this._deletePlate.bind(this),
        hidden: !this.canDelete,
      },
    ];
  }
}
